/**
 * A collapsible section with a count — "Match fonts (1)" in the screenshots.
 *
 * NATIVE `<details>`, not state. The section opens and closes without React re-rendering the
 * form around it, it is keyboard-operable and announced correctly with no ARIA to maintain,
 * and it survives being rendered inside a form that re-renders on every keystroke elsewhere.
 * The only cost is hiding the default marker, which takes two rules because `::marker` and
 * `::-webkit-details-marker` are both still in play.
 *
 * It is for the settings that only SOME documents need. A PDF with every font installed should
 * not show a font-matching panel at all, and one with twelve missing fonts should not push the
 * Import button off the screen — collapsed-by-default is what makes both true.
 */

import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

/*
 * `chakra('details')`, not `Box as="details"`. The polymorphic `as` still renders the right
 * element but types its props against a div, so `open` is rejected — the factory knows it is a
 * `<details>` and types `open` with it.
 */
const Details = chakra('details');

export interface DisclosureProps {
  label: string;
  /** Shown as a small chip beside the label — how many things are inside. */
  count?: number | undefined;
  /** Open on first render. Left closed by default, which is the point of the control. */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Disclosure({ label, count, defaultOpen = false, children }: DisclosureProps) {
  return (
    <Details
      open={defaultOpen || undefined}
      borderRadius="md"
      borderWidth="1px"
      borderColor="figma.border"
      overflow="hidden"
    >
      <Flex
        as="summary"
        cursor="pointer"
        align="center"
        justify="space-between"
        px="2.5"
        py="1.5"
        fontSize="2xs"
        fontWeight="500"
        color="figma.textSecondary"
        listStyleType="none"
        _marker={{ display: 'none' }}
        css={{ '&::-webkit-details-marker': { display: 'none' } }}
        _hover={{ bg: 'figma.panelHover' }}
        transition="background 0.15s"
      >
        <Flex align="center" gap="1.5">
          {label}
          {count !== undefined && (
            <Text
              fontSize="2xs"
              px="1"
              borderRadius="sm"
              bg="figma.panelHover"
              color="figma.textMuted"
              lineHeight="1.3"
            >
              {count}
            </Text>
          )}
        </Flex>
        <Box color="figma.textMuted" display="flex" alignItems="center">
          <ChevronDown size={11} />
        </Box>
      </Flex>
      <Box px="2.5" pb="2">
        {children}
      </Box>
    </Details>
  );
}
